/**
 * The one hard floor in the update system.
 *
 * Every other update path (Capgo auto-update, the update pill, the App
 * Store banner) is a *soft nudge* the user can ignore or never trigger.
 * This is the only mechanism that can require a user to move: the backend
 * serves `min_bundle` on /api/public/mobile-version, and when the running
 * OTA bundle (`__APP_VERSION__` from package.json) is below it, the
 * MinimumVersionGate shows a non-dismissible "please update" screen.
 *
 * It stays dormant on every normal release — `min_bundle` is `null` by
 * default. You raise it BY HAND, deliberately, only after shipping a
 * backend change that would break an older bundle.
 *
 * CRITICAL: this must fail OPEN. A null/absent/garbage floor, or a
 * missing/garbage running version, means "don't gate". A network blip
 * fetching the floor must never lock a working app out — the gate simply
 * doesn't render, exactly as if no floor were set. `isBelowMinBundle`
 * returns `true` ONLY when it can prove the running bundle is strictly
 * older than a well-formed floor.
 */

/** Parse a dotted version ("1.2.23") into numeric parts, or null if it
 *  isn't a plausible semver (empty, non-numeric, no dots-or-digits). */
function parseVersion(v: unknown): number[] | null {
  if (typeof v !== "string") return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(".");
  const nums: number[] = [];
  for (const p of parts) {
    // Reject anything that isn't a plain non-negative integer segment —
    // a pre-release tag like "1.2.0-beta" or junk shouldn't be coerced
    // into a comparison that could wrongly gate (or wrongly pass) a user.
    if (!/^\d+$/.test(p)) return null;
    nums.push(parseInt(p, 10));
  }
  return nums.length ? nums : null;
}

/**
 * True iff `current` is a well-formed version strictly older than the
 * well-formed floor `min`. Any ambiguity (unparseable input on either
 * side, null/empty floor) returns false — fail open.
 */
export function isBelowMinBundle(current: unknown, min: unknown): boolean {
  const cur = parseVersion(current);
  const floor = parseVersion(min);
  if (!cur || !floor) return false;
  const len = Math.max(cur.length, floor.length);
  for (let i = 0; i < len; i++) {
    const a = cur[i] ?? 0;
    const b = floor[i] ?? 0;
    if (a < b) return true;
    if (a > b) return false;
  }
  return false; // equal → at the floor, allowed
}
